/**
 * TocHelpModal — 위키 목차(TOC) 도움말 모달
 *
 * 위키 페이지 상세보기의 우측 TOC 패널에서 [?] 버튼으로 열리는 도움말.
 * 목차에 어떤 요소가 표시되는지, 헤딩을 어떻게 작성하는지 설명한다.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { ListTree, Hash, Keyboard, MousePointerClick, Sparkles } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const TocHelpModal: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-body font-semibold text-foreground">
            <ListTree className="size-[18px] text-primary" />
            <Trans>Table of Contents Help</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>Guide on what appears in the table of contents on the right side of wiki pages and how to create headings.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* 1. 목차에 표시되는 것 */}
          <section className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Hash className="size-3.5 text-muted-foreground" />
              <h3 className="text-meta-sm font-semibold text-foreground uppercase tracking-wider">
                <Trans>What appears in the table of contents?</Trans>
              </h3>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <p className="text-label text-foreground leading-relaxed">
                <Trans>Only <strong>headings</strong> in the content are automatically shown in the table of contents. Indentation shows the level (H1·H2·H3).</Trans>
              </p>
              <p className="text-meta-sm text-muted-foreground leading-relaxed">
                <Trans>Lists, tables, images, quotes, callouts, and other blocks are not included. To quickly jump to a section, add a <strong>heading</strong> before it.</Trans>
              </p>
            </div>
          </section>

          {/* 2. 헤딩 작성 방법 */}
          <section className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Keyboard className="size-3.5 text-muted-foreground" />
              <h3 className="text-meta-sm font-semibold text-foreground uppercase tracking-wider">
                <Trans>How to create headings</Trans>
              </h3>
            </div>
            <div className="rounded-lg border border-border bg-card divide-y divide-border">
              <div className="flex items-start gap-3 p-3">
                <span className="inline-flex shrink-0 items-center justify-center min-w-[64px] h-[22px] px-2 rounded-md bg-primary/10 text-primary text-meta-sm font-semibold">
                  <Trans>Toolbar</Trans>
                </span>
                <p className="text-label text-foreground leading-relaxed flex-1">
                  <Trans>Click the <strong>H1</strong> or <strong>H2</strong> button in the editor menu bar. Select text first, then click to convert it to a heading.</Trans>
                </p>
              </div>
              <div className="flex items-start gap-3 p-3">
                <span className="inline-flex shrink-0 items-center justify-center min-w-[64px] h-[22px] px-2 rounded-md bg-primary/10 text-primary text-meta-sm font-semibold">
                  <Trans>Shortcut</Trans>
                </span>
                <div className="flex-1 space-y-1">
                  <p className="text-label text-foreground leading-relaxed">
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">Ctrl</kbd>
                    <span className="mx-0.5">+</span>
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">Alt</kbd>
                    <span className="mx-0.5">+</span>
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">1</kbd>
                    <span className="ml-1.5">→ H1</span>
                  </p>
                  <p className="text-label text-foreground leading-relaxed">
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">Ctrl</kbd>
                    <span className="mx-0.5">+</span>
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">Alt</kbd>
                    <span className="mx-0.5">+</span>
                    <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">2</kbd>
                    <span className="ml-1.5">→ H2</span>
                  </p>
                  <p className="text-meta-sm text-muted-foreground"><Trans>On macOS: <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">⌃</kbd> + <kbd className="inline-flex items-center px-1.5 py-px text-meta-sm font-mono rounded bg-muted border border-border">⌥</kbd> + number</Trans></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3">
                <span className="inline-flex shrink-0 items-center justify-center min-w-[64px] h-[22px] px-2 rounded-md bg-primary/10 text-primary text-meta-sm font-semibold">
                  <Trans>Markdown</Trans>
                </span>
                <div className="flex-1 space-y-1">
                  <p className="text-label text-foreground leading-relaxed">
                    <Trans>Type <code className="px-1 py-px rounded bg-muted text-foreground font-mono text-meta-sm">#</code>, <code className="px-1 py-px rounded bg-muted text-foreground font-mono text-meta-sm">##</code>, or <code className="px-1 py-px rounded bg-muted text-foreground font-mono text-meta-sm">###</code> at the start of a line, then press <strong>Space</strong> → H1, H2, H3</Trans>
                  </p>
                  <p className="text-meta-sm text-muted-foreground leading-relaxed"><Trans>e.g. Type <code className="px-1 py-px rounded bg-muted font-mono text-meta-sm">## Agenda</code> then Space → "Agenda" becomes H2</Trans></p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. 미리보기 — 실제 목차가 어떻게 보이는지 */}
          <section className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-muted-foreground" />
              <h3 className="text-meta-sm font-semibold text-foreground uppercase tracking-wider">
                <Trans>Example</Trans>
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* 본문 예시 */}
              <div className="rounded-lg border border-border bg-card p-3">
                <div className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2"><Trans>Body</Trans></div>
                <div className="space-y-1.5">
                  <div className="text-h4 font-bold text-foreground"># <Trans>Project overview</Trans></div>
                  <div className="text-body font-bold text-foreground pl-2">## <Trans>Schedule</Trans></div>
                  <div className="text-label font-semibold text-foreground pl-4">### Phase 1</div>
                  <div className="text-meta-sm text-muted-foreground pl-4 italic">(<Trans>Body paragraph — not shown in TOC</Trans>)</div>
                  <div className="text-label font-semibold text-foreground pl-4">### Phase 2</div>
                  <div className="text-body font-bold text-foreground pl-2">## <Trans>Members</Trans></div>
                </div>
              </div>
              {/* 목차 결과 */}
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <ListTree className="size-3 text-primary" />
                  <div className="text-meta-sm font-semibold text-primary uppercase tracking-wider"><Trans>Table of contents</Trans></div>
                </div>
                <div className="space-y-1 text-meta text-foreground">
                  <div><Trans>Project overview</Trans></div>
                  <div className="pl-3"><Trans>Schedule</Trans></div>
                  <div className="pl-6">Phase 1</div>
                  <div className="pl-6">Phase 2</div>
                  <div className="pl-3"><Trans>Members</Trans></div>
                </div>
              </div>
            </div>
          </section>

          {/* 4. 팁 */}
          <section className="space-y-2">
            <div className="flex items-center gap-1.5">
              <MousePointerClick className="size-3.5 text-muted-foreground" />
              <h3 className="text-meta-sm font-semibold text-foreground uppercase tracking-wider">
                <Trans>Tips</Trans>
              </h3>
            </div>
            <ul className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5 list-disc list-inside text-label text-foreground leading-relaxed marker:text-muted-foreground">
              <li><Trans>Click an item in the table of contents to smoothly scroll to that position.</Trans></li>
              <li><Trans>If you often reference different spots on the same page, add an H2 / H3 before that area.</Trans></li>
              <li><Trans>Empty heading text is not shown in the table of contents.</Trans></li>
              <li><Trans>Too many nested levels make the table of contents hard to read — up to H3 is recommended.</Trans></li>
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TocHelpModal;
